'use client';

import { publicClient } from '@/api/client/public.client';
import { usePublicVideos } from '@/api/queries/freemium.queries';
import { queryKeys } from '@/api/query-keys';
import type { Video } from '@/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export interface CreatorGuestSummary {
  /** First-page items, cross-referenced — suitable for peek thumbs. */
  lockedVideos: Video[];
  /** First-page items, cross-referenced — suitable for peek thumbs. */
  lockedShorts: Video[];
  /** First-page items from `/public/public-videos`. */
  freeVideos: Video[];
  /**
   * Real locked-long count derived from `pagination.total`. Subtracts the
   * entire free-videos pagination total (endpoint returns mixed types), so
   * this can undercount by the number of free shorts — bounded, usually 0-2.
   * BE fix: add `types` filter to `/public/public-videos`.
   */
  totalLocked: number;
  /** Real locked-shorts count; same skew caveat as `totalLocked`. */
  totalLockedShorts: number;
}

/**
 * Guest-only derivation of locked vs free content for a creator.
 * Cross-references `getMapVideos` (full catalog) against `usePublicVideos`
 * (free only). TanStack query keys match `CreatorHomeTab`'s usage so the
 * cache dedupes across the layout, the home tab, and the ticket band.
 */
export function useCreatorGuestSummary(
  creatorId: number | undefined,
  enabled: boolean
): CreatorGuestSummary {
  const mapFilters = useMemo(() => ({ creators: creatorId ?? 0 }), [creatorId]);
  const shortsFilters = useMemo(() => ({ creators: creatorId ?? 0, types: 'shorts' }), [creatorId]);

  const { data: mapResponse } = useQuery({
    queryKey: queryKeys.videos.mapList(mapFilters),
    queryFn: () => publicClient.getMapVideos(mapFilters),
    staleTime: 1000 * 60 * 5,
    enabled: enabled && !!creatorId,
  });

  const { data: mapShortsResponse } = useQuery({
    queryKey: queryKeys.videos.mapList(shortsFilters),
    queryFn: () => publicClient.getMapVideos(shortsFilters),
    staleTime: 1000 * 60 * 5,
    enabled: enabled && !!creatorId,
  });

  const { data: publicData } = usePublicVideos(
    enabled && creatorId ? { creators: creatorId } : undefined
  );

  return useMemo<CreatorGuestSummary>(() => {
    const freeVideos = publicData?.videos ?? [];
    const freeTotal = publicData?.pagination?.total ?? freeVideos.length;
    const freeUuids = new Set(freeVideos.map((v) => v.uuid));

    const mapVideos = mapResponse?.videos ?? [];
    const mapTotal = mapResponse?.pagination?.total ?? mapVideos.length;
    const mapShorts = mapShortsResponse?.videos ?? [];
    const mapShortsTotal = mapShortsResponse?.pagination?.total ?? mapShorts.length;

    return {
      freeVideos,
      lockedVideos: mapVideos.filter((v) => !freeUuids.has(v.uuid)),
      lockedShorts: mapShorts.filter((v) => !freeUuids.has(v.uuid)),
      totalLocked: Math.max(0, mapTotal - freeTotal),
      totalLockedShorts: Math.max(0, mapShortsTotal - freeTotal),
    };
  }, [mapResponse, mapShortsResponse, publicData]);
}
